import { api } from './client';
import type { CappedRows } from './types';
import { i18nMap } from '@/lib/i18n-map';

export interface EconomyData {
  month: string;
  range: { from: string; to: string };
  pl: {
    /** Which of the two the module is counting on. */
    basis: 'incasat' | 'prestat';
    /** The one the basis selects — every screen reads this, not the pair. */
    revenue: number;
    collected: number;
    billed: number;
    /** Finished appointments in the month — the divisor for a per-visit figure. */
    done: number;
    materials: number;
    lab: number;
    commission: number;
    /** One-off outgoings recorded in the month. */
    expenses: number;
    /** Standing monthly costs owed for it. */
    fixedMonthly: number;
    /** Dobânda creditelor pentru luna asta — capitalul rambursat nu e cost. */
    loanInterest: number;
    /** Fondul din statul de plată al lunii — aceeași cifră ca fila Personal. */
    payroll: number;
    /** Cât din cheltuielile punctuale stă în categoria „Salarii" (semnal de dublare). */
    salaryExpenses: number;
    /** The two together — what the month costs regardless of who came in. */
    fixedTotal: number;
    directCost: number;
    profit: number;
    margin: number;
    taxRate: number;
    tax: number;
    profitAfterTax: number;
  };
  perDoctor: Array<{
    doctorId: string | null;
    doctor: string;
    collected: number;
    commissionPct: number;
    commission: number;
  }>;
  series: Array<{
    month: string;
    collected: number;
    /** Baza clinicii — încasat sau prestat, ca antetul P&L-ului. */
    revenue: number;
    /** Tot ce a ieșit: cheltuieli, fixe, dobândă, marfă, laborator, comisioane. */
    expenses: number;
    /** Din care: cheltuieli punctuale + fixe + dobândă. */
    fixed: number;
    /** Din care: marfă + laborator + comisioane. */
    direct: number;
  }>;
  /** The year so far — January to the shown month, inclusive. */
  ytd: { months: number; collected: number; profit: number };
  capacity: {
    bookedMinutes: number;
    availableMinutes: number;
    doctors: number;
    workdays: number;
    /** The average chair-day, in minutes, behind `availableMinutes`. */
    dayMinutes: number;
    usage: number;
    revenuePerHour: number;
    revenuePerAvailableHour: number;
  };
  receivables: number;
  /** Luna e predată: cifrele de mai sus sunt cele de la închidere. */
  closed: { closedAt: string; closedBy: string | null } | null;
  /**
   * How much of the month is actually marked. Every figure on these screens is
   * derived from appointments marked finished and paid; when few are, the
   * screens are reporting the marking, not the month.
   */
  quality: { appointments: number; donePct: number; paidPct: number };
  /**
   * Next month's profit: the plan for it when the clinic has written one,
   * otherwise the average of the last three months. Same cost model as the
   * P&L, so the two cannot disagree.
   */
  forecast: { month: string; basis: 'plan' | 'trend'; profit: number };
}

export interface PlanLines {
  revenue: number;
  materials: number;
  lab: number;
  commission: number;
  expenses: number;
}

export type BudgetLimits = Omit<PlanLines, 'revenue'>;

export interface EconomyPlan {
  month: string;
  annualGoal: number;
  plan: PlanLines;
  budget: BudgetLimits;
}

/** Named to mirror the DTO: sending anything else fails the whole request. */
export interface EconomyPlanInput {
  month: string;
  plan: PlanLines;
  budget?: BudgetLimits;
  annualGoal?: number;
}

export interface MarketingReport {
  month: string;
  /** Registrul plus ce a tastat clinica — ce se folosește pentru CAC și ROI. */
  spend: number;
  /** Din cheltuielile lunii, categoria „Marketing". Nu se poate edita aici. */
  spendRecorded: number;
  /** Ce a tastat clinica pe lângă registru. Asta se editează. */
  spendTyped: number;
  newPatients: number;
  treated: number;
  collected: number;
  /** null when no patient arrived: a cost per nothing is not a number. */
  cac: number | null;
  avgCheckBasis: 'perService' | 'perPatient';
  visits: number;
  revenuePerPatient: number;
  ltv: { patients: number; avgValue: number; avgVisits: number; repeatRate: number };
}

export interface Formulas {
  revenueBasis: 'incasat' | 'prestat';
  costMat: boolean;
  costComm: boolean;
  costOvh: boolean;
  commissionSource: 'avg' | 'fixed';
  commFixed: number;
  overheadMethod: 'perMinute' | 'perVisit' | 'percent';
  overheadPercent: number;
  projectionDays: number;
  inflowConfirmed: boolean;
  inflowScheduled: boolean;
  inflowPlans: boolean;
  /** Costul propriu al lucrărilor programate intră în proiecția de numerar. */
  projVarCost: boolean;
  /** Luna curentă, sau media ultimelor trei. */
  calcBase: 'month' | 'avg3';
  /**
   * Ziua din lună pe care o propune un cost fix nou.
   *
   * Aplicația veche avea o singură zi pe clinică, pentru toate plățile fixe.
   * Aici fiecare cost își poartă ziua lui — chiria pe 5, salariile pe 25 — deci
   * valoarea asta e doar punctul de pornire al unui rând nou, nu o regulă care
   * suprascrie ce s-a stabilit deja.
   */
  fixedDay: number;
  showupRate: number;
  marginGood: number;
  marginOk: number;
  avgCheckBasis: 'perService' | 'perPatient';
  taxRate: number;
}

export interface PaymentCalendar {
  from: string;
  to: string;
  /** Câte zile s-au proiectat — orizontul din „Formule", sau cel cerut. */
  days: number;
  entries: Array<{
    due: string;
    label: string;
    amount: number;
    overdue: boolean;
    direction: 'in' | 'out';
  }>;
  totalIn: number;
  totalOut: number;
  net: number;
  overdueCount: number;
}

export interface AnalyticsPeriod {
  appointments: number;
  /** Cât de plină a fost clinica: minutele programate / orele lucrate. */
  occupancy: number;
  done: number;
  noshow: number;
  cancelled: number;
  /** `rapoarte.financiare` (RAP-2) — `null` for a caller without it. */
  billed: number | null;
  /** `rapoarte.financiare` (RAP-2) — `null` for a caller without it. */
  collected: number | null;
  newPatients: number;
  /**
   * Lucrări finalizate cu preț 0 sau lipsă.
   *
   * Nu intră în `billed`, deci venitul afișat e mai mic decât munca făcută.
   * Ecranul o spune, altfel cifra pare pur și simplu mică.
   */
  noPrice: number;
  noshowRate: number;
  /** Planuri de tratament propuse în perioadă (primul rând al planului). */
  plansProposed: number;
  /** Dintre ele: bifate „acceptat” sau cu lucrul început (un rând finalizat). */
  plansAccepted: number;
  /**
   * Dintre propuse: refuzate de pacient și neîncepute. Rămân în numitor — au
   * fost propuse și n-au fost alese —, deci conversia nu se schimbă; cifra
   * spune doar cât din rest e un „nu" spus, și cât e încă nehotărât.
   */
  plansDeclined: number;
  /** Acceptate ÷ propuse, în procente, cu o zecimală. */
  planConversion: number;
  /** `rapoarte.financiare` (RAP-2) — `null` for a caller without it. */
  plansProposedValue: number | null;
  /** `rapoarte.financiare` (RAP-2) — `null` for a caller without it. */
  plansAcceptedValue: number | null;
  /** `rapoarte.financiare` (RAP-2) — `null` for a caller without it. */
  plansDeclinedValue: number | null;
}

export interface AnalyticsPatients {
  total: number;
  returning: number;
  retentionRate: number;
  newByMonth: Array<{ month: string; count: number }>;
  /** `value` needs `rapoarte.financiare` (RAP-2) — `null` for a caller without it. */
  topValue: Array<{ name: string; value: number | null }>;
}

export interface AnalyticsData {
  range: { from: string; to: string; days: number };
  previousRange: { from: string; to: string };
  current: AnalyticsPeriod;
  previous: AnalyticsPeriod;
  byWeekday: Array<{ dow: number; count: number }>;
  byStatus: Array<{ status: string; count: number }>;
  peakHour: { hour: number; count: number } | null;
  heatmap: Array<{ dow: number; hour: number; count: number }>;
  noshowByWeekday: Array<{ dow: number; count: number }>;
  patients: AnalyticsPatients;
}

/** One row of a report that is a log rather than an aggregate. */
export interface ReceptionRow {
  date: string;
  time: string;
  patient: string;
  /** RAP-7: rândul duce la fișa pacientului; null fără fișă legată. */
  patientId: string | null;
  phone: string | null;
  doctor: string | null;
  service: string | null;
  status: string;
  price: string;
  paid: boolean;
}

export interface CashRow {
  date: string;
  patient: string | null;
  /** RAP-7; null la o încasare fără fișă legată. */
  patientId: string | null;
  doctor: string | null;
  service: string | null;
  method: string;
  amount: string;
  reversed: boolean;
  operator: string | null;
  /** Cine a adus pacientul — „Ghid" în raportul original. */
  guide: string | null;
}

export interface SourceRow {
  source: string;
  patients: string;
  /** `rapoarte.financiare` (RAP-2) — `null` for a caller without it. */
  revenue: string | null;
}

/**
 * Datorii pe vechime: what is owed, by how long since the work was finished.
 * The four add up to `due` on a row, and to `DebtAging.total` across the clinic.
 */
export interface AgingSplit {
  d0_30: number;
  d31_60: number;
  d61_90: number;
  d90: number;
}

export interface DebtRow extends AgingSplit {
  patientId: string;
  patient: string;
  phone: string | null;
  doctor: string | null;
  procedures: string;
  due: string;
  oldest: string | null;
}

/** The whole clinic's aging — not cut by the list's ceiling. */
export interface DebtAging {
  buckets: AgingSplit;
  total: number;
}

/** Rapoarte → Echipă: one person's figures for the period. */
export interface TeamKpiRow {
  /** `staffId`, or `name:<name>` for a figure matched only by a typed name. */
  key: string;
  staffId: string | null;
  name: string;
  role: string | null;
  active: boolean | null;
  /** Some of the row was attributed by a typed name (the stock register). */
  byName: boolean;
  apptCreated: number;
  apptConfirmed: number;
  apptRescheduled: number;
  apptCancelled: number;
  apptNoshow: number;
  payments: number;
  /** `rapoarte.financiare` — `null` for a caller without it. */
  paymentsAmount: number | null;
  dayCloses: number;
  dueCalls: number;
  assisted: number;
  consumed: number;
  delivered: number;
  cycles: number;
  kits: number;
}

export interface TeamKpiReport {
  from: string;
  to: string;
  /** `own`: the caller sees only their own row. */
  scope: 'all' | 'own';
  money: boolean;
  rows: TeamKpiRow[];
}

export interface DoctorReportRow {
  doctorId: string;
  doctorName: string;
  spec: string | null;
  appointments: string;
  done: string;
  noshow: string;
  /** `rapoarte.financiare` (RAP-2) — `null` for a caller without it. */
  revenue: string | null;
  bookedMin: string;
  daysWorked: string;
  dayMin: string;
  /**
   * Booked minutes over the hours of the days actually worked — never over a
   * capacity nobody set. A doctor in on two days of thirty is not 6% busy.
   */
  occupancy: number;
}

export interface ServiceByDoctorRow {
  doctor: string;
  service: string;
  count: string;
  /** `rapoarte.financiare` (RAP-2) — `null` for a caller without it. */
  revenue: string | null;
}

export interface PayrollRow {
  doctorId: string | null;
  doctor: string;
  collected: string;
  payments: string;
  sessions: string;
}

/**
 * The counting rubrics of form 039-1/e — mirrors the API catalogue; a
 * tariff line is mapped to one of these in Setari > Servicii.
 */
export const F039_RUBRICS = [
  { key: 'consultatie', label: 'Consultații' },
  { key: 'igienizare', label: 'Igienizări / detartraj' },
  { key: 'profilaxie', label: 'Proceduri profilactice (fluorizare, sigilare)' },
  { key: 'obturatie', label: 'Obturații aplicate' },
  { key: 'endodontie', label: 'Tratamente endodontice (canale)' },
  { key: 'extractie', label: 'Dinți extrași' },
  { key: 'chirurgie', label: 'Alte intervenții chirurgicale' },
  { key: 'protetica', label: 'Lucrări protetice' },
  { key: 'ortodontie', label: 'Aparate și activări ortodontice' },
  { key: 'radiologie', label: 'Examinări radiologice' },
  { key: 'alta', label: 'Alte lucrări' },
] as const;

export const F039_RUBRIC_LABELS: Record<string, string> = Object.fromEntries(
  F039_RUBRICS.map((r) => [r.key, r.label]),
);
/** Lines whose service has no rubric land here — visible, not dropped. */
F039_RUBRIC_LABELS.nealocat = 'Nealocat unei rubrici';

export interface F039Doctor {
  doctorId: string | null;
  doctorName: string | null;
  visits: { total: number; primare: number; secundare: number; profilactice: number };
  patients: number;
  /** `amount` and `amountTotal` need `rapoarte.financiare` (RAP-2) — `null`
   *  for a caller without it; visit and patient counts stay. */
  rubrics: Array<{ rubric: string; procedures: number; amount: number | null }>;
  amountTotal: number | null;
}

export interface F039Report {
  from: string;
  to: string;
  doctors: F039Doctor[];
  /** Registrul fișelor 043/e generate în perioadă, cea mai nouă sus. */
  register: Array<{
    /** The sheet's id. */
    id: string;
    /** Numărul fișei, unic în clinică. */
    no: string;
    /** Numărul tipărit pe hârtie, la o fișă generată înainte de numerotarea pe fișă. */
    printedNo: string | null;
    generatedAt: string;
    generatedBy: string | null;
    patientId: string;
    patientName: string;
    /** Null la fișa generală a pacientului. */
    visitId: string | null;
    visitDate: string | null;
    doctorName: string | null;
    visitType: string | null;
    diagnostic: string | null;
    icd10: string | null;
    services: string | null;
    amount: number | null;
    sign: string;
    consent: boolean;
    hasPdf: boolean;
  }>;
  registerTruncated: boolean;
  /** Vizite finalizate în perioadă fără nicio fișă generată. */
  visitsWithoutSheet: number;
}

// ── Form 039-1/e — the stored monthly register ──────────────────────────────

export type F039Variant = 'terapeut' | 'ortodont' | 'chirurg' | 'protetician';

/** A column as frozen into a generated sheet. */
export interface F039GridColumn {
  no: number;
  label: string;
  /** Nested header groups above the leaf, outer-first. */
  group: string[];
  source: { kind: string };
  money: boolean;
}

export interface F039Grid {
  variant: F039Variant;
  title: string;
  approval: string;
  hasCategory: boolean;
  year: number;
  month: number;
  days: number;
  columns: F039GridColumn[];
  rows: Array<{ day: number; values: Array<number | null> }>;
  totals: Array<number | null>;
  hasMoney: boolean;
}

export interface F039SheetMeta {
  id: string;
  no: string;
  variant: F039Variant;
  doctorId: string | null;
  doctorName: string | null;
  doctorCategory: string | null;
  year: number;
  month: number;
  hasMoney: boolean;
  printedNo: string | null;
  generatedAt: string;
  generatedByName: string | null;
  hasPdf: boolean;
}

export interface F039SheetView extends F039SheetMeta {
  grid: F039Grid;
}

export interface F039DoctorOption {
  id: string;
  name: string;
  spec: string | null;
  variant: F039Variant;
}

export const F039_VARIANT_LABELS: Record<F039Variant, string> = {
  terapeut: 'Stomatolog (terapeut)',
  ortodont: 'Ortodont',
  chirurg: 'Chirurg',
  protetician: 'Protetician',
};

/** One generated 043/e sheet, as the clinic-wide register lists it. */
export type Fisa043RegisterEntry = F039Report['register'][number];

/** Registrul fișelor 043/e pe o perioadă — `GET /reports/fisa043/register`. */
export interface Fisa043Register {
  from: string;
  to: string;
  register: Fisa043RegisterEntry[];
  /** Only the newest 500 came back. */
  registerTruncated: boolean;
  /** Completed visits in the period that have no sheet generated yet. */
  visitsWithoutSheet: number;
}

/** One line of form 069/e — the register of outpatient interventions. */
export interface F069Row {
  nr: number;
  visitId: string;
  patientId: string;
  date: string;
  patientName: string;
  idnp: string | null;
  birthDate: string | null;
  diagnostic: string | null;
  icd10: string | null;
  anesthesia: string | null;
  intervention: string;
  surgeon: string | null;
  assistant: string | null;
}

export interface F069Register {
  year: number;
  month: number;
  from: string;
  to: string;
  rows: F069Row[];
}

export interface F069SheetMeta {
  id: string;
  no: string;
  year: number;
  month: number;
  institution: string | null;
  cabinet: string | null;
  rowCount: number;
  generatedAt: string;
  generatedByName: string | null;
  hasPdf: boolean;
}

export interface F069SheetView extends F069SheetMeta {
  register: F069Register;
}

/** O linie din fișa de programare 040/e. */
export interface F040Row {
  appointmentId: string;
  patientId: string;
  scheduled: string;
  arrived: string;
  insured: string;
  patientName: string;
  birthDate: string | null;
  sex: string;
  ageCategory: number | null;
  address: string | null;
  visitKind: 'I' | 'II' | 'P';
  status: string;
}

export interface F040Sheet {
  date: string;
  doctorId: string;
  doctorName: string | null;
  cabinet: string | null;
  institution: string | null;
  rows: F040Row[];
}

/** O linie din lista zilnică 037/e. */
export interface F037Row {
  visitId: string;
  patientId: string;
  time: string;
  patientName: string;
  age: number | null;
  address: string | null;
  icd10: string | null;
  diagnostic: string | null;
  works: string;
  visitType: string;
  ucm: number;
}

export interface F037Sheet {
  date: string;
  doctorId: string;
  doctorName: string | null;
  doctorSpec: string | null;
  institution: string | null;
  rows: F037Row[];
  totals: { patients: number; visits: number; ucm: number };
}

/** O linie din registrul adresărilor 074/e. */
export interface F074Row {
  nr: number;
  visitId: string;
  patientId: string;
  date: string;
  time: string;
  insured: string;
  firstOrRepeat: '1' | '2';
  patientName: string;
  idnp: string | null;
  sex: string;
  birthDate: string | null;
  address: string | null;
  workplace: string | null;
  diagnostic: string | null;
  icd10: string | null;
  treatment: string;
  note: string;
}

export interface F074Register {
  year: number;
  month: number;
  from: string;
  to: string;
  rows: F074Row[];
}

export interface F074SheetMeta {
  id: string;
  no: string;
  year: number;
  month: number;
  institution: string | null;
  rowCount: number;
  generatedAt: string;
  generatedByName: string | null;
  hasPdf: boolean;
}

export interface F074SheetView extends F074SheetMeta {
  register: F074Register;
}

/**
 * A field marked `| null` is a money figure the API strips for a caller
 * without `incasari.vede` (and, where noted, `financiar.vede` too) — this is
 * the landing screen every signed-in user gets, so the narrowing happens on
 * the server rather than trusting each screen to hide what it read anyway.
 */
export interface Dashboard {
  date: string;
  today: {
    appointments: number;
    done: number;
    noshow: number;
    remaining: number;
    /** `incasari.vede`. */
    revenue: number | null;
    /** `incasari.vede`. */
    expectedRevenue: number | null;
  };
  week: { appointments: number; /** `incasari.vede`. */ revenue: number | null };
  month: {
    /** `incasari.vede`. */
    revenue: number | null;
    /** Recorded expenses plus goods bought this month. `incasari.vede` AND `financiar.vede`. */
    spend: number | null;
    /** Revenue less that spend — not accounting profit; salaries and rent are
     *  elsewhere. `incasari.vede` AND `financiar.vede`, same as `spend`. */
    net: number | null;
    newPatients: number;
  };
  /** The figures the KPI catalogue needs and today's appointments cannot give. */
  counts: {
    activePatients: number;
    ongoingTx: number;
    toConfirm: number;
    /** `incasari.vede`. */
    avgTicket: number | null;
    branches: number;
  };
  /** Today's remaining and tomorrow's, with the reason each is at risk. */
  noshowRisk: Array<{
    id: string;
    date: string;
    hour: number;
    min: number;
    patientName: string;
    reasons: string[];
  }>;
  alerts: {
    lowStock: number;
    expiringLots: number;
    /** `incasari.vede`. */
    overdueInstallments: number | null;
    /** `incasari.vede`. */
    unpaidProcedures: number | null;
    /** Programări cerute din portal, încă neconfirmate — nu cereri de materiale. */
    pendingRequests: number;
    /** Cereri de concediu nedecise. */
    pendingLeave: number;
    /** Acte de personal care expiră în 60 de zile. */
    expiringStaffDocs: number;
    /** Lucrări de laborator cu termenul depășit. */
    overdueLab: number;
    /** Oameni în concediu aprobat azi. */
    onLeaveToday: number;
    /** Fișele de vizită ale medicului rămase în ciornă. */
    myDraftVisits: number;
    /** Lucrările de laborator întârziate ale medicului. */
    myOverdueLab: number;
  };
  /** Gol pentru cine n-are drept pe pacienți. */
  upcoming: Array<{
    id: string;
    time: string;
    patientName: string;
    doctorName: string | null;
    service: string | null;
    status: string;
  }>;
  /** Primele cinci cereri din portal, pentru cine confirmă programările. */
  portalRequests: Array<{
    id: string;
    date: string;
    time: string;
    patientName: string;
    doctorName: string | null;
  }>;
  /** Primele cinci fișe ale medicului rămase în ciornă. */
  draftVisits: Array<{ id: string; patientId: string; patientName: string; date: string }>;
}

export const WIDGET_PERIODS = ['azi', '7zile', 'luna', '30zile'] as const;
export type WidgetPeriod = (typeof WIDGET_PERIODS)[number];

export const WIDGET_PERIOD_LABELS = i18nMap<WidgetPeriod>('common', {
  azi: 'azi',
  '7zile': 'ultimele7Zile',
  luna: 'lunaCurenta',
  '30zile': 'ultimele30DeZile',
});

/**
 * The forms an indicator can be drawn as.
 *
 * `number` is the figure and every indicator has it. `bare`, `linie` and
 * `arie` draw the daily series; `structura`, `placinta` and `inel` draw the
 * split into categories. Which of them an indicator offers is its own
 * business — the API says so in `charts` — because a pie of one number is not
 * a chart and a line through a breakdown is not a line.
 */
export const WIDGET_VIZ = [
  'number',
  'bare',
  'linie',
  'arie',
  'structura',
  'placinta',
  'inel',
] as const;
export type WidgetViz = (typeof WIDGET_VIZ)[number];

export const WIDGET_VIZ_LABELS = i18nMap<WidgetViz>('common', {
  number: 'cifra',
  bare: 'bare',
  linie: 'linie',
  arie: 'arie',
  structura: 'barePeCategorii',
  placinta: 'placinta',
  inel: 'inel',
});

export interface WidgetDef {
  type: string;
  label: string;
  description: string;
  category: 'financiar' | 'activitate' | 'pacienti' | 'atentie' | 'personal';
  permission: string;
  money: boolean;
  supportsPeriod: boolean;
  supportsDoctor: boolean;
  charts: WidgetViz[];
  /** What the categories of a breakdown are, for the chart's own subtitle. */
  breakdownLabel?: string;
}

/**
 * Cât loc ocupă un indicator pe panou: 1, 2, 2×2 coloane, sau toată lățimea.
 *
 * Trepte, nu pixeli. O mărime în pixeli s-ar strica la prima fereastră de
 * altă lățime; treptele sunt multipli de coloană, deci se așază singure
 * oriunde, iar golurile lăsate de plăcile mari le umplu cele mici.
 */
export const WIDGET_SIZES = ['mic', 'mediu', 'mare', 'banda'] as const;
export type WidgetSize = (typeof WIDGET_SIZES)[number];

export const WIDGET_SIZE_LABELS = i18nMap<WidgetSize>('common', {
  mic: 'mic',
  mediu: 'lat',
  mare: 'mare',
  banda: 'banda',
});

export interface PanelWidget {
  id: string;
  type: string;
  period?: WidgetPeriod | null;
  doctorId?: string | null;
  viz?: WidgetViz;
  size?: WidgetSize;
}

export interface WidgetData {
  id: string;
  type: string;
  label: string;
  period: WidgetPeriod | null;
  doctorId: string | null;
  doctorName: string | null;
  viz: WidgetViz;
  money: boolean;
  missing: boolean;
  value: number;
  /** Aceeași cifră pentru perioada dinainte, sau `null` dacă n-are perioadă. */
  previous: number | null;
  /** Ce înseamnă o creștere aici: „bine" verde, „rau" roșu, „neutru" fără culoare. */
  trend: 'bine' | 'rau' | 'neutru';
  subtitle: string | null;
  series: Array<{ day: string; value: number }> | null;
  breakdown: Array<{ label: string; value: number }> | null;
}

export const WIDGET_CATEGORY_LABELS = i18nMap<WidgetDef['category']>('common', {
  financiar: 'financiar',
  activitate: 'activitate',
  pacienti: 'pacienti',
  atentie: 'deAtentie',
  personal: 'personal',
});

export interface PerformanceRow {
  doctorId: string;
  doctorName: string;
  patients: number;
  visits: number;
  done: number;
  noshow: number;
  /** `rapoarte.financiare` (RAP-2) — `null` for a caller without it. */
  collected: number | null;
  perVisit: number | null;
  hours: number;
  perHour: number | null;
}

export interface PatientProcedureRow {
  id: string;
  /** RAP-7: fișa pacientului rândului. */
  patientId: string;
  date: string | null;
  procedure: string;
  category: string | null;
  doctor: string | null;
  patient: string;
  registered: string | null;
  source: string | null;
  /** Every money field below needs `rapoarte.financiare` (RAP-2) — `null`
   *  for a caller without it. */
  cost: number | null;
  listCost?: number | null;
  discountPct?: number | null;
  paid: number | null;
  due: number | null;
}

export interface MaterialCostRow {
  service: string;
  works: number;
  cost: number;
  revenue: number;
  avgCost: number;
  avgPrice: number;
  margin: number | null;
}

export interface PricingRow {
  id: string;
  name: string;
  price: number;
  dur: number;
  materials: number;
  commission: number;
  overhead: number;
  cost: number;
  margin: number;
  marginPct: number;
  revenue: number;
}

export interface PricingReport {
  month: string;
  marginOk: number;
  calcBase: 'month' | 'avg3';
  marginGood: number;
  avgCommission: number;
  overheadPerMinute: number;
  bookedMinutes: number;
  fixed: number;
  fixedSoFar: number;
  rows: PricingRow[];
  belowCost: number;
  thin: number;
  reprice: Array<PricingRow & { lost: number }>;
}

export interface EconomyLiability {
  id: string;
  name: string;
  /** Suma datorată, pentru o datorie simplă. La un credit rămâne 0. */
  amount: number;
  kind?: 'simpla' | 'credit';
  principal?: number;
  annualRate?: number;
  termMonths?: number;
  startsOn?: string | null;
  /** Ziua din lună a ratei. */
  payDay?: number;

  // Calculate de API pentru un credit, ca ecranul să nu refacă anuitatea.

  /** Cât se mai datorează azi — soldul, nu suma împrumutată. */
  owed?: number;
  /** Rata lunară. */
  payment?: number;
  /** Dobânda lunii curente — singura parte care e cheltuială. */
  interest?: number;
  /** Soldul vine din contractul de împrumut din Contabilitate, nu din graficul de aici. */
  fromBooks?: boolean;
}

export interface EconomyConfig {
  cash: number;
  assets: Array<{
    id: string;
    name: string;
    value: number;
    residual: number;
    lifeMonths: number;
    boughtOn: string | null;
    disposedOn: string | null;
    /** `YYYY-MM-DD`, when it is next due for service; the bell warns ahead of it. */
    nextServiceOn?: string | null;
    /** Months between services; „Revizie făcută" moves the date on by this. */
    serviceIntervalMonths?: number | null;
  }>;
  liabilities: EconomyLiability[];
  /** What the system knows on its own, so the balance sheet has one source. */
  derived: {
    stockValue: number;
    receivables: number;
    supplierDebt: number;
    labDebt: number;
  };
}

export type EconomyConfigInput = Omit<EconomyConfig, 'derived'>;

/** O datorie fără câmpurile calculate de API — exact forma pe care o primește LiabilityDto. */
export function storedLiability(l: EconomyLiability): EconomyLiability {
  const { id, name, amount, kind, principal, annualRate, termMonths, startsOn, payDay } = l;
  const out: EconomyLiability = { id, name, amount };
  if (kind !== undefined) out.kind = kind;
  if (principal !== undefined) out.principal = principal;
  if (annualRate !== undefined) out.annualRate = annualRate;
  if (termMonths !== undefined) out.termMonths = termMonths;
  if (startsOn !== undefined) out.startsOn = startsOn;
  if (payDay !== undefined) out.payDay = payDay;
  return out;
}

/** Ce a mai rămas de verificat după o migrare — vezi `ImportHealthService`. */
export interface ImportHealth {
  imported: boolean;
  patients: number;
  upcoming: number;
  teethToFill: number;
  servicesWithoutPrice: number;
  duplicatePairs: number;
}

export type SetupStepKey =
  | 'clinic'
  | 'specialties'
  | 'schedule'
  | 'services'
  | 'prices'
  | 'team'
  | 'patient'
  | 'appointment';

/** Mirrors `SetupChecklist` in `apps/api/src/modules/reports/setup-checklist.service.ts`. */
export interface SetupChecklistData {
  show: boolean;
  steps: { key: SetupStepKey; done: boolean }[];
}

export const reportsApi = {
  dashboard: () => api.get<Dashboard>('/reports/dashboard'),

  f039Doctors: () => api.get<F039DoctorOption[]>('/reports/f039/doctors'),
  f039Generate: (input: { variant: F039Variant; doctorId: string; year: number; month: number }) =>
    api.post<F039SheetView>('/reports/f039/sheets', input),
  f039Sheets: (filters: { year?: number; month?: number; doctorId?: string; variant?: string }) =>
    api.get<F039SheetMeta[]>('/reports/f039/sheets', { query: filters }),
  f039Sheet: (id: string) => api.get<F039SheetView>(`/reports/f039/sheets/${id}`),
  /** Coloanele „manual" (rurali) ale unei fișe fără PDF păstrat; totalul îl refac serverul. */
  f039SetManual: (id: string, cells: Array<{ column: number; day: number; value: number }>) =>
    api.patch<F039SheetView>(`/reports/f039/sheets/${id}/manual`, { cells }),
  f039SavePdf: (id: string, pdf: Blob) => {
    const form = new FormData();
    form.append('file', pdf, `f039-${id}.pdf`);
    return api.post<F039SheetMeta>(`/reports/f039/sheets/${id}/pdf`, form);
  },
  f039PdfUrl: (id: string) => api.get<{ url: string }>(`/reports/f039/sheets/${id}/pdf-url`),
  f039DownloadPdf: (id: string, no: string) =>
    api.download(`/reports/f039/sheets/${id}/pdf`, `f039-nr-${no}.pdf`),

  /** Registrul fișelor 043/e generate în perioadă, cea mai nouă sus. */
  fisa043Register: (from: string, to: string) =>
    api.get<Fisa043Register>('/reports/fisa043/register', { query: { from, to } }),

  /** Formularul 069/e — cere și `fisa.vede`, și `pacienti.vede_toti`. */
  f069Generate: (input: { year: number; month: number; cabinet?: string | null }) =>
    api.post<F069SheetView>('/reports/f069/sheets', input),
  f069Sheets: (filters: { year?: number; month?: number } = {}) =>
    api.get<F069SheetMeta[]>('/reports/f069/sheets', { query: filters }),
  f069Sheet: (id: string) => api.get<F069SheetView>(`/reports/f069/sheets/${id}`),
  f069SavePdf: (id: string, pdf: Blob) => {
    const form = new FormData();
    form.append('file', pdf, `f069-${id}.pdf`);
    return api.post<F069SheetMeta>(`/reports/f069/sheets/${id}/pdf`, form);
  },
  f069PdfUrl: (id: string) => api.get<{ url: string }>(`/reports/f069/sheets/${id}/pdf-url`),
  f069DownloadPdf: (id: string, no: string) =>
    api.download(`/reports/f069/sheets/${id}/pdf`, `f069-nr-${no}.pdf`),

  /** Fișa de programare 040/e — ziua unui medic; nu se păstrează nimic. */
  f040: (doctorId: string, date: string, cabinet?: string) =>
    api.get<F040Sheet>('/reports/f040', { query: { doctorId, date, cabinet } }),
  /** Lista zilnică 037/e a unui medic. */
  f037: (doctorId: string, date: string) =>
    api.get<F037Sheet>('/reports/f037', { query: { doctorId, date } }),

  /** Registrul adresărilor 074/e — lunar, numerotat și păstrat. */
  f074Generate: (input: { year: number; month: number }) =>
    api.post<F074SheetView>('/reports/f074/sheets', input),
  f074Sheets: (filters: { year?: number; month?: number } = {}) =>
    api.get<F074SheetMeta[]>('/reports/f074/sheets', { query: filters }),
  f074Sheet: (id: string) => api.get<F074SheetView>(`/reports/f074/sheets/${id}`),
  f074SavePdf: (id: string, pdf: Blob) => {
    const form = new FormData();
    form.append('file', pdf, `f074-${id}.pdf`);
    return api.post<F074SheetMeta>(`/reports/f074/sheets/${id}/pdf`, form);
  },
  f074PdfUrl: (id: string) => api.get<{ url: string }>(`/reports/f074/sheets/${id}/pdf-url`),
  f074DownloadPdf: (id: string, no: string) =>
    api.download(`/reports/f074/sheets/${id}/pdf`, `f074-nr-${no}.pdf`),

  panel: () => api.get<{ widgets: PanelWidget[]; catalog: WidgetDef[] }>('/reports/panel'),
  savePanel: (widgets: PanelWidget[]) =>
    api.put<{ widgets: PanelWidget[] }>('/reports/panel', { widgets }),
  panelData: () => api.get<WidgetData[]>('/reports/panel/data'),

  paymentCalendar: (days?: number) =>
    api.get<PaymentCalendar>('/reports/payment-calendar', { query: { days } }),

  closeMonth: (month: string) =>
    api.post<{ month: string; closedAt: string; closedBy: string }>(
      `/reports/economy/close/${month}`,
      {},
    ),
  reopenMonth: (month: string) => api.delete<{ ok: boolean }>(`/reports/economy/close/${month}`),

  economy: (month: string) => api.get<EconomyData>('/reports/economy', { query: { month } }),

  dashConfig: () => api.get<{ kpis?: string[]; sections?: string[] }>('/reports/dashboard/config'),
  /** „Primii pași" of a new clinic; needs `setari.modifica`. */
  dashSetup: () => api.get<SetupChecklistData>('/reports/dashboard/setup'),
  /** Ce a mai rămas de verificat după ce clinica și-a adus datele. */
  importHealth: () => api.get<ImportHealth>('/reports/dashboard/import-health'),
  saveDashConfig: (config: { kpis: string[]; sections: string[] }) =>
    api.put<{ kpis: string[]; sections: string[] }>('/reports/dashboard/config', config),
  /** Șterge personalizările de panou ale tuturor membrilor unui rol (Cabinete personale). */
  clearRoleDashConfigs: (role: string) =>
    api.delete<{ cleared: number }>(`/reports/dashboard/config/role/${role}`),

  analytics: (from: string, to: string) =>
    api.get<AnalyticsData>('/reports/analytics', { query: { from, to } }),

  logReception: (from: string, to: string) =>
    api.get<CappedRows<ReceptionRow>>('/reports/log/reception', { query: { from, to } }),
  logCash: (from: string, to: string) =>
    api.get<CappedRows<CashRow>>('/reports/log/cash', { query: { from, to } }),
  sources: (from: string, to: string) =>
    api.get<SourceRow[]>('/reports/sources', { query: { from, to } }),
  // Plafonată, ca celelalte liste: ecranul spune „primele N din M".
  debts: () =>
    api.get<{ rows: DebtRow[]; total: number; cap: number; aging: DebtAging }>('/reports/debts'),
  team: (from: string, to: string) =>
    api.get<TeamKpiReport>('/reports/team', { query: { from, to } }),
  servicesByDoctor: (from: string, to: string) =>
    api.get<ServiceByDoctorRow[]>('/reports/services-by-doctor', { query: { from, to } }),
  payroll: (from: string, to: string) =>
    api.get<PayrollRow[]>('/reports/payroll', { query: { from, to } }),

  revenue: (from?: string, to?: string) =>
    api.get<Array<{ day: string; cash: string; card: string; transfer: string; total: string }>>(
      '/reports/revenue',
      { query: { from, to } },
    ),

  doctors: (from?: string, to?: string) =>
    api.get<DoctorReportRow[]>('/reports/doctors', { query: { from, to } }),

  economyPlan: (month: string) =>
    api.get<EconomyPlan>('/reports/economy/plan', { query: { month } }),
  saveEconomyPlan: (body: EconomyPlanInput) => api.put<EconomyPlan>('/reports/economy/plan', body),

  marketing: (month: string) =>
    api.get<MarketingReport>('/reports/economy/marketing', { query: { month } }),
  saveMarketingSpend: (month: string, amount: number) =>
    api.put<MarketingReport>('/reports/economy/marketing', { month, amount }),

  formulas: () => api.get<Formulas>('/reports/economy/formulas'),
  saveFormulas: (body: Formulas) => api.put<Formulas>('/reports/economy/formulas', body),

  economyConfig: () => api.get<EconomyConfig>('/reports/economy/config'),
  saveEconomyConfig: (body: EconomyConfigInput) =>
    api.put<EconomyConfig>('/reports/economy/config', {
      ...body,
      // Doar ce se salvează: `owed`, `payment` și `interest` le calculează API-ul
      // la citire. Trimise înapoi așa cum veniseră, validarea (forbidNonWhitelisted)
      // le refuza cu 400 — orice clinică cu măcar o datorie nu-și mai putea salva
      // nici mijloacele fixe, nici bilanțul.
      liabilities: body.liabilities.map(storedLiability),
    }),
  /** A fixed asset was serviced today: the server moves its next service on. */
  assetServiced: (assetId: string) =>
    api.post<EconomyConfig>(`/reports/economy/assets/${encodeURIComponent(assetId)}/serviced`),

  pricing: (month: string) => api.get<PricingReport>('/reports/pricing', { query: { month } }),

  patientProcedures: (from: string, to: string) =>
    api.get<CappedRows<PatientProcedureRow>>('/reports/patient-procedures', {
      query: { from, to },
    }),
  materialCost: (from: string, to: string) =>
    api.get<MaterialCostRow[]>('/reports/material-cost', { query: { from, to } }),

  performance: (from: string, to: string) =>
    api.get<PerformanceRow[]>('/reports/performance', { query: { from, to } }),

  services: (from?: string, to?: string) =>
    // `revenue`: `rapoarte.financiare` (RAP-2) — `null` for a caller without it.
    api.get<Array<{ service: string; count: string; revenue: string | null }>>(
      '/reports/services',
      { query: { from, to } },
    ),
};
